import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

/** AI-driven next-word predictor. Calls /predict and renders 5 suggestion pills. */
export default function PredictiveBar({ profileId, strip, tilesByLabel, onPick, enabled, lang = "en" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!enabled || !profileId) { setSuggestions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const labels = strip.map((s) => s.speak_text || s.label);
        const { data } = await api.post(`/profiles/${profileId}/predict`, { strip: labels });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      }
      setBusy(false);
    }, strip.length === 0 ? 0 : 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [profileId, strip, enabled]);

  if (!enabled) return null;

  return (
    <div className="flex items-center gap-2 mt-3" data-testid="predictive-bar">
      <span className="display text-sm font-semibold uppercase tracking-[0.2em] text-[color:var(--ink-soft)] flex items-center gap-1">
        <Sparkles size={14} className="text-[color:var(--coral)]" />
        {t(lang, "strip.suggested")}
      </span>
      <div className="flex flex-wrap gap-2 flex-1">
        {busy && suggestions.length === 0 && (
          <span className="text-sm text-[color:var(--ink-soft)] italic">thinking…</span>
        )}
        {suggestions.map((label, i) => {
          const tile = tilesByLabel.get(label.toLowerCase());
          if (!tile) return null;
          return (
            <button
              key={`${label}-${i}`}
              type="button"
              onClick={() => onPick(tile)}
              className="px-3 py-2 rounded-2xl border-[3px] border-[color:var(--ink)] bg-white shadow-[3px_3px_0_0_var(--ink)] flex items-center gap-2 hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform"
              data-testid={`predict-suggestion-${i}`}
            >
              <span className="text-xl" aria-hidden>{tile.emoji}</span>
              <span className="display font-semibold text-[color:var(--ink)]">{tile.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
